import React from 'react';
import { Typography, Button, Slider, Space, Segmented } from 'antd';
import { useTranslation } from 'react-i18next';
import GeofenceMap from './GeofenceMap';
import {
  IGeofenceZone,
  IGeofencePoint,
  IGeofenceGeometry,
  GeofenceZoneKind,
} from '../interfaces/heavyDutyMachinery/IGeofence';
import { SURFACE_1_SOFT } from '../theme/creamyTokens';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const { Text } = Typography;

export const MIN_RADIUS_METRES = 50;
export const MAX_RADIUS_METRES = 5_000;
export const MIN_POLYGON_POINTS = 3;
export const DEFAULT_RADIUS_METRES = 500;

/** Where a brand-new shape opens when there is nothing at all to centre it on. */
export const FALLBACK_LATITUDE = 40.4168;
export const FALLBACK_LONGITUDE = -3.7038;

/**
 * Whether the shape is finished enough to send. Deliberately the same rule the
 * backend applies to a zone AND to a move request -- approving a request turns
 * it into the zone, so a shape it would refuse as one must not be offered as
 * the other.
 */
export const isGeometryComplete = (draft: IGeofenceZone): boolean =>
  draft.kind === 'Polygon'
    ? draft.points.length >= MIN_POLYGON_POINTS
    : draft.centerLatitude != null && draft.centerLongitude != null;

/** The draft as the backend wants it: only the half that Kind makes true. */
export const toGeometry = (draft: IGeofenceZone): IGeofenceGeometry =>
  draft.kind === 'Polygon'
    ? { kind: 'Polygon', points: draft.points }
    : {
        kind: 'Circle',
        centerLatitude: draft.centerLatitude,
        centerLongitude: draft.centerLongitude,
        radiusMetres: draft.radiusMetres ?? DEFAULT_RADIUS_METRES,
      };

/** The middle of a set of corners, for keeping the map still when the shape changes. */
export const averageOf = (points: IGeofencePoint[]): IGeofencePoint | undefined =>
  points.length === 0
    ? undefined
    : {
        latitude: points.reduce((sum, point) => sum + point.latitude, 0) / points.length,
        longitude: points.reduce((sum, point) => sum + point.longitude, 0) / points.length,
      };

/**
 * The same corners, moved so their middle lands somewhere else.
 *
 * Used to seed a guest's move proposal: they get the shape the host already
 * agreed to, over where the machine actually is, instead of a blank map and
 * an invitation to invent a site. They can still redraw it if the new plot is
 * a different shape.
 */
export const translatePoints = (points: IGeofencePoint[], latitude: number, longitude: number): IGeofencePoint[] => {
  const middle = averageOf(points);
  if (!middle) return [];
  return points.map((point) => ({
    latitude: point.latitude - middle.latitude + latitude,
    longitude: point.longitude - middle.longitude + longitude,
  }));
};

export interface IGeofenceZoneEditorProps {
  /** The shape being edited. The parent owns it; this only ever hands back a new one. */
  draft: IGeofenceZone;
  onChange: (draft: IGeofenceZone) => void;

  machineLatitude?: number;
  machineLongitude?: number;

  /** Drawn dashed underneath: the zone as it stands while a move is being proposed, or the proposal while the zone stands. */
  referenceZone?: IGeofenceZone;

  /** One line above the map saying what the dashed outline is, when there is one. */
  referenceHint?: string;

  height?: number;
}

/**
 * Drawing a work zone: a circle by tapping its centre, or a polygon by tapping
 * its corners.
 *
 * The host uses it on the booking to set the zone, and the guest uses it in the
 * move-request dialog to say where they need to work instead -- the same
 * editor both times, because a move request becomes the zone the moment it is
 * approved, and the two must not be able to express different things.
 */
const GeofenceZoneEditor: React.FC<IGeofenceZoneEditorProps> = ({
  draft,
  onChange,
  machineLatitude,
  machineLongitude,
  referenceZone,
  referenceHint,
  height,
}) => {
  const { t } = useTranslation();

  const isPolygon = draft.kind === 'Polygon';
  const setPoints = (points: IGeofencePoint[]) => onChange({ ...draft, points });

  const handleKindChange = (kind: GeofenceZoneKind) => {
    if (kind === draft.kind) return;

    // Changing shape always starts the new one empty rather than converting
    // one into the other. A circle turned into a square, or a square into the
    // circle around it, is a fence nobody drew -- and somebody can be accused
    // of crossing it.
    if (kind === 'Polygon') {
      onChange({ ...draft, kind, points: [] });
      return;
    }

    // Back to a circle: keep the map where the work was happening, using the
    // middle of whatever corners had been placed.
    const middle = averageOf(draft.points);
    onChange({
      ...draft,
      kind,
      points: [],
      centerLatitude: middle?.latitude ?? draft.centerLatitude ?? machineLatitude ?? FALLBACK_LATITUDE,
      centerLongitude: middle?.longitude ?? draft.centerLongitude ?? machineLongitude ?? FALLBACK_LONGITUDE,
      radiusMetres: draft.radiusMetres ?? DEFAULT_RADIUS_METRES,
    });
  };

  return (
    <>
      <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 10 }}>
        {translate(t, isPolygon ? TRANSLATIONS.GEOFENCE_POLYGON_HINT : TRANSLATIONS.GEOFENCE_CIRCLE_HINT)}
      </Text>

      {referenceZone && referenceHint && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
          {referenceHint}
        </Text>
      )}

      <Segmented
        block
        size="small"
        style={{ marginBottom: 10 }}
        value={draft.kind}
        onChange={(value) => handleKindChange(value as GeofenceZoneKind)}
        options={[
          { label: translate(t, TRANSLATIONS.GEOFENCE_SHAPE_CIRCLE), value: 'Circle' },
          { label: translate(t, TRANSLATIONS.GEOFENCE_SHAPE_POLYGON), value: 'Polygon' },
        ]}
      />

      <GeofenceMap
        zone={draft}
        referenceZone={referenceZone}
        machineLatitude={machineLatitude}
        machineLongitude={machineLongitude}
        height={height}
        onCenterPicked={
          isPolygon ? undefined : (latitude, longitude) => onChange({ ...draft, centerLatitude: latitude, centerLongitude: longitude })
        }
        onPointsChanged={isPolygon ? setPoints : undefined}
      />

      {!isPolygon && (
        <div style={{ background: SURFACE_1_SOFT, borderRadius: 12, padding: '10px 14px', marginTop: 12 }}>
          <Text style={{ fontSize: 13, display: 'block', marginBottom: 2 }}>
            {translate(t, TRANSLATIONS.GEOFENCE_RADIUS_LABEL)}: {Math.round(draft.radiusMetres ?? DEFAULT_RADIUS_METRES)} m
          </Text>
          <Slider
            min={MIN_RADIUS_METRES}
            max={MAX_RADIUS_METRES}
            step={50}
            value={draft.radiusMetres ?? DEFAULT_RADIUS_METRES}
            onChange={(value) => onChange({ ...draft, radiusMetres: value })}
            tooltip={{ open: false }}
          />
        </div>
      )}

      {/* Drawing a polygon is the one thing here done wholly by hand, so it
          gets the two ways out of a wrong tap: the last one, or all of them. */}
      {isPolygon && (
        <div style={{ background: SURFACE_1_SOFT, borderRadius: 12, padding: '10px 14px', marginTop: 12 }}>
          <Text style={{ fontSize: 13, display: 'block' }}>
            {translate(t, TRANSLATIONS.GEOFENCE_POLYGON_COUNT, { count: draft.points.length })}
          </Text>
          {!isGeometryComplete(draft) && (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
              {translate(t, TRANSLATIONS.GEOFENCE_POLYGON_NEEDS_MORE)}
            </Text>
          )}
          <Space style={{ marginTop: 10 }} wrap>
            <Button size="small" shape="round" disabled={draft.points.length === 0} onClick={() => setPoints(draft.points.slice(0, -1))}>
              {translate(t, TRANSLATIONS.GEOFENCE_POLYGON_UNDO)}
            </Button>
            <Button size="small" shape="round" disabled={draft.points.length === 0} onClick={() => setPoints([])}>
              {translate(t, TRANSLATIONS.GEOFENCE_POLYGON_CLEAR)}
            </Button>
          </Space>
        </div>
      )}
    </>
  );
};

export default GeofenceZoneEditor;
